import { useEffect, useRef } from "react";
import type { CreatedTrip } from "@mtform/shared";
import { Button } from "@/components/ui/button";

interface SuccessScreenProps {
  trips: CreatedTrip[];
  onCreateAnother: () => void;
}

// =============================================================================
// SuccessScreen
//
// Replaces the form once a booking is accepted. The tick draws itself (the
// same finite, reduced-motion-safe keyframes as the sign-in "check your
// email" screen), the trip numbers sit in chips, and the next action is one
// clear button.
// =============================================================================

export function SuccessScreen({ trips, onCreateAnother }: SuccessScreenProps) {
  // The submit button used to have focus; after success the form is
  // replaced in-place. Pull focus to the heading so screen readers
  // announce the success copy instead of the user being stranded on
  // a now-removed button.
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <section
      role="status"
      aria-live="polite"
      className="mx-auto flex max-w-xl flex-col items-center gap-6 px-4 py-12 text-center sm:py-20"
    >
      <div className="login-pop relative">
        <span
          aria-hidden="true"
          className="login-check-halo absolute inset-0 rounded-full bg-success/40"
        />
        <svg viewBox="0 0 64 64" className="relative h-20 w-20" aria-hidden="true">
          <circle cx="32" cy="32" r="28" className="login-check-circle" />
          <path d="M20 33 L28 41 L44 23" className="login-check-mark" />
        </svg>
      </div>
      <div className="flex flex-col gap-2">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-2xl font-extrabold leading-tight tracking-tight text-foreground focus:outline-none sm:text-3xl"
        >
          Thank you for your reservation!
        </h1>
        <p className="text-[15px] text-muted-foreground">
          We will review your booking and send you a confirmation email shortly.
        </p>
      </div>
      {trips.length > 0 ? (
        <ul className="flex flex-wrap justify-center gap-2">
          {trips.map((t, i) => (
            <li
              key={`${t.friendlyId}-${i}`}
              className="rounded-lg border border-border bg-foreground/[0.04] px-3 py-1.5 font-mono text-sm font-semibold text-foreground"
            >
              {`Trip ${t.friendlyId}${t.leg ? ` (Leg ${t.leg})` : ""}`}
            </li>
          ))}
        </ul>
      ) : null}
      <Button onClick={onCreateAnother} size="lg" className="login-cta mt-1 h-12 rounded-2xl px-7">
        Create another trip
      </Button>
    </section>
  );
}
